'use client'

import React, { useState } from 'react'
import { useApi } from '@excellent/ui/api'
import { lens } from '@excellent/ui/api/contracts/lens'
import type { Intent } from "@excellent/ui"
import { Badge, IntentBadge } from "@excellent/ui"
import { Count } from "@excellent/ui"
import { Card, CardContent } from "@excellent/ui"
import { Skeleton } from "@excellent/ui"
import { Alert, AlertDescription } from "@excellent/ui"
import { Tabs, TabsList, TabsTrigger } from "@excellent/ui"
import { PageHeader, SearchInput } from "@excellent/ui"
import { AlertTriangle, Circle, CircleDot, CircleCheck } from "@excellent/ui/icons"
import { formatDate } from '@lens/lib/decode'

interface TodoItem {
 id?: string
 content?: string
 status?: string
 priority?: string
 [key: string]: unknown
}

interface TodoFile {
 name: string
 data: unknown
 mtime: string
}

type FilterType = 'all' | 'pending' | 'in_progress' | 'completed'

function parseTodos(data: unknown): TodoItem[] {
 if (Array.isArray(data)) return data as TodoItem[]
 if (data && typeof data === 'object' && 'todos' in data)
 return parseTodos((data as { todos: unknown }).todos)
 return []
}

function normalizeStatus(s?: string): string {
 return s ?? 'pending'
}

// Each priority gets a DISTINCT intent so the pills are visually separable.
const PRIORITY_INTENT: Record<string, Intent> = {
 high: 'danger',
 medium: 'warning',
 low: 'neutral',
}

const STATUS_META: Record<string, { icon: React.ElementType; intent: Intent; color: string; label: string }> = {
 pending: { icon: Circle, intent: 'neutral', color: 'text-muted-foreground', label: 'Pending' },
 in_progress: { icon: CircleDot, intent: 'warning', color: 'text-warning', label: 'In Progress' },
 completed: { icon: CircleCheck, intent: 'success', color: 'text-success', label: 'Completed' },
}

function TodoRow({ item, file }: { item: TodoItem; file: TodoFile }) {
 const status = normalizeStatus(item.status)
 const meta = STATUS_META[status] ?? STATUS_META.pending!
 const Icon = meta.icon
 const isCompleted = status === 'completed'

 return (
 <Card className="py-0">
 <CardContent className="px-4 py-3.5 flex items-start gap-3">
 <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${meta.color}`} />
 <div className="flex-1 min-w-0">
 <p className={`type-body leading-body ${isCompleted ? 'line-through text-foreground' : 'text-foreground'}`}>
 {String(item.content ?? JSON.stringify(item))}
 </p>
 <div className="flex flex-wrap items-center gap-2 mt-2">
 <IntentBadge intent={meta.intent} className="type-body">
 {meta.label}
 </IntentBadge>
 {item.priority && (
 <IntentBadge
 intent={PRIORITY_INTENT[item.priority] ?? 'neutral'}
 className="type-body"
 >
 {item.priority}
 </IntentBadge>
 )}
 <Badge variant="outline" className="type-body">
 {file.name}
 </Badge>
 <span className="type-body text-muted-foreground">
 {formatDate(file.mtime)}
 </span>
 </div>
 </div>
 </CardContent>
 </Card>
 )
}

export default function TodosPage() {
 const { data, error, isLoading } = useApi(lens.todos, {}) as { data: { todos: TodoFile[] } | undefined; error: unknown; isLoading: boolean }
 const [search, setSearch] = useState('')
 const [filter, setFilter] = useState<FilterType>('all')

 const todos = data?.todos ?? []
 const allItems: Array<{ file: TodoFile; item: TodoItem }> = todos.flatMap(file =>
 parseTodos(file.data).map(item => ({ file, item }))
 )

 const counts = {
 all: allItems.length,
 pending: allItems.filter(x => normalizeStatus(x.item.status) === 'pending').length,
 in_progress: allItems.filter(x => normalizeStatus(x.item.status) === 'in_progress').length,
 completed: allItems.filter(x => normalizeStatus(x.item.status) === 'completed').length,
 }

 const filtered = allItems.filter(({ item }) => {
 if (filter !== 'all' && normalizeStatus(item.status) !== filter) return false
 if (search && !String(item.content ?? '').toLowerCase().includes(search.toLowerCase())) return false
 return true
 })

 return (
 <div className="flex flex-col">
 <div className="space-y-5">

 <PageHeader
 title="Todos"
 description={
 data && (search || filter !== 'all')
 ? `${filtered.length} of ${allItems.length} todos`
 : undefined
 }
 toolbar={
 data ? (
 <SearchInput
 value={search}
 onChange={e => setSearch(e.target.value)}
 placeholder="Search todos…"
 />
 ) : undefined
 }
 />

 {error ? (
 <Alert variant="destructive">
 <AlertTriangle className="h-4 w-4" />
 <AlertDescription>Error loading data: {String(error)}</AlertDescription>
 </Alert>
 ) : null}

 {isLoading && (
 <div className="space-y-3">
 <Skeleton className="h-10 w-full rounded-lg" />
 {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
 </div>
 )}

 {data && (
 <>
 {/* Filter tabs */}
 <Tabs value={filter} onValueChange={v => setFilter(v as FilterType)}>
 <TabsList className="w-full sm:w-auto">
 <TabsTrigger value="all" className="gap-2">
 All
 <Count value={counts.all} />
 </TabsTrigger>
 <TabsTrigger value="pending" className="gap-2">
 <Circle className="w-3.5 h-3.5" />
 Pending
 <Count value={counts.pending} />
 </TabsTrigger>
 <TabsTrigger value="in_progress" className="gap-2">
 <CircleDot className="w-3.5 h-3.5 text-warning" />
 In Progress
 <Count value={counts.in_progress} />
 </TabsTrigger>
 <TabsTrigger value="completed" className="gap-2">
 <CircleCheck className="w-3.5 h-3.5 text-success" />
 Done
 <Count value={counts.completed} />
 </TabsTrigger>
 </TabsList>
 </Tabs>

 {filtered.length === 0 ? (
 <div className="text-center py-16">
 <CircleCheck className="w-8 h-8 mx-auto mb-3 text-foreground" />
 <p className="text-foreground type-body">
 {allItems.length === 0
 ? 'No todos found in ~/.claude/todos/'
 : 'No todos match your filter.'}
 </p>
 </div>
 ) : (
 <div className="space-y-2">
 {filtered.map(({ file, item }, i) => (
 <TodoRow key={i} item={item} file={file} />
 ))}
 </div>
 )}
 </>
 )}
 </div>
 </div>
 )
}
